import {
  useSignClientUserConsents as useSignClientUserConsentsBase,
  ConsentType,
  getAgreementLinkItemFromConsentType as getAgreementLinkItemFromConsentTypeBase,
  ConsentOrigin,
  EVERNORTH_CONSENT_TYPES,
  useCreateExternalEvernorthPatient as useCreateExternalEvernorthPatientBase,
  ClientUserParentOrGuardianInput,
} from "@grow-therapy-team/seedling-components";
import { GraphQLFormattedError } from "graphql";
import { useCallback, useMemo, useState } from "react";
import { useTrackIntroRoomEvent as useTrackIntroRoomEventBase } from "../page-content/useTrackIntroRoomEvent";
import { logger } from "../../../datadog/logger";
import { EventTypeEnum } from "../../../segment/types";
import { normalizeError } from "../../../utils";
import { SignConsentsVariables } from "./useRequiredAgreements";

type UseSignConsentsProps = {
  consentSigningToken?: string | null;
  onSubmissionError?: () => void;
  onSubmissionSuccess: () => void;
};

type Dependencies = {
  getAgreementLinkItemFromConsentType: typeof getAgreementLinkItemFromConsentTypeBase;
  logError: typeof logger.error;
  useCreateExternalEvernorthPatient: typeof useCreateExternalEvernorthPatientBase;
  useSignClientUserConsents: typeof useSignClientUserConsentsBase;
  useTrackIntroRoomEvent: typeof useTrackIntroRoomEventBase;
};

/**
 * Convenience hook wrapper around the useSignClientUserConsents mutation hook.
 * Manages mutation context, execution, and error handling.
 *
 * For Evernorth consents, this hook will also create an external Evernorth
 * patient for the client. Errors from either of these steps are considered
 * critical failures and will result in the `onSubmissionError` callback being
 * invoked.
 *
 * @param props - Primary props for the hook:
 * - consentSigningToken: The single-use auth token to use for signing the
 *   required agreements. This is only applicable if the client used PII
 *   verification instead of logging in.
 * - onSubmissionError: Optional callback to invoke when the mutation returns errors.
 * - onSubmissionSuccess: The callback to invoke when the mutation succeeds.
 * @param dependencies - Optional overrides for the dependencies of the hook.
 * @returns The signConsents function, the hasError state, and the isSubmitting
 * state.
 */
export function useSignConsents(
  {
    consentSigningToken,
    onSubmissionError,
    onSubmissionSuccess,
  }: UseSignConsentsProps,
  {
    getAgreementLinkItemFromConsentType,
    logError,
    useCreateExternalEvernorthPatient,
    useSignClientUserConsents,
    useTrackIntroRoomEvent,
  }: Dependencies = {
    getAgreementLinkItemFromConsentType:
      getAgreementLinkItemFromConsentTypeBase,
    logError: logger.error,
    useCreateExternalEvernorthPatient: useCreateExternalEvernorthPatientBase,
    useSignClientUserConsents: useSignClientUserConsentsBase,
    useTrackIntroRoomEvent: useTrackIntroRoomEventBase,
  },
) {
  const { formSubmitted } = useTrackIntroRoomEvent();

  const mutationContext = useMemo(
    () =>
      consentSigningToken
        ? {
            // Apply a custom auth header for the consent signing token
            skipAuth: true,
            headers: { Authorization: `ConsentToken ${consentSigningToken}` },
          }
        : undefined,
    [consentSigningToken],
  );

  const [signClientUserConsents, { loading: isSubmitting }] =
    useSignClientUserConsents({ context: mutationContext });

  const [
    createExternalEvernorthPatient,
    { loading: isCreatingExternalEvernorthPatient },
  ] = useCreateExternalEvernorthPatient({
    context: { skipAuth: true },
  });

  const [hasError, setHasError] = useState<boolean>(false);

  const handleError = useCallback(
    ({
      clientUserShortId,
      consentTypes,
      flowId,
      errors,
    }: {
      clientUserShortId: string;
      consentTypes: ConsentType[];
      flowId: string;
      errors: GraphQLFormattedError[];
    }) => {
      logError("Error signing client user consents", {
        clientUserShortId,
        consentTypes,
        flowId,
        errors: errors.map((error) => error.message),
      });
      setHasError(true);
      onSubmissionError?.();
    },
    [logError, onSubmissionError],
  );

  const signConsents = useCallback(
    async ({
      clientUserShortId,
      consentTypes,
      flowId,
      clientUserParentGuardianShortId,
      clientUserParentOrGuardian,
    }: SignConsentsVariables) => {
      formSubmitted(
        EventTypeEnum.CLIENT_NEW_INTRO_ROOM_REQUIRED_AGREEMENTS_SIGNED,
        { flowId },
      );
      setHasError(false);

      try {
        const { errors: signClientUserConsentsErrors } =
          await signClientUserConsents({
            variables: {
              clientUserShortId,
              values: {
                consentOrigin: ConsentOrigin.TELEHEALTH_WAITING_ROOM_BLOCK,
                signedConsents: consentTypes.map((consentType) => {
                  return {
                    consentType,
                    consentFilePath:
                      getAgreementLinkItemFromConsentType(consentType).href,
                  };
                }),
                clientUserParentGuardianShortId,
                clientUserParentOrGuardian:
                  // casting here because relationship field starts as an empty string, but will be prevented from submitting on the frontend when empty
                  clientUserParentOrGuardian as ClientUserParentOrGuardianInput,
              },
            },
          });

        let createExternalEvernorthPatientErrors:
          | readonly GraphQLFormattedError[]
          | undefined;
        if (hasEvernorthConsents(consentTypes)) {
          const { errors } = await createExternalEvernorthPatient({
            variables: {
              clientUserShortId,
              createConsentsIfMissing: false,
            },
          });
          createExternalEvernorthPatientErrors = errors;
        }

        if (
          signClientUserConsentsErrors ||
          createExternalEvernorthPatientErrors
        ) {
          handleError({
            clientUserShortId,
            consentTypes,
            flowId,
            errors: [
              ...(signClientUserConsentsErrors || []),
              ...(createExternalEvernorthPatientErrors || []),
            ],
          });
          return;
        }

        onSubmissionSuccess();
      } catch (error) {
        handleError({
          clientUserShortId,
          consentTypes,
          flowId,
          errors: [normalizeError(error)],
        });
      }
    },
    [
      createExternalEvernorthPatient,
      formSubmitted,
      getAgreementLinkItemFromConsentType,
      handleError,
      onSubmissionSuccess,
      signClientUserConsents,
    ],
  );

  return {
    hasError,
    isSubmitting: isSubmitting || isCreatingExternalEvernorthPatient,
    signConsents,
  };
}

function hasEvernorthConsents(consentTypes: ConsentType[]): boolean {
  return consentTypes.some((consentType) =>
    // Widen the readonly tuple to accept ConsentType as a valid argument
    (EVERNORTH_CONSENT_TYPES as readonly ConsentType[]).includes(consentType),
  );
}
